import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { mockSubmitForm } from '../data/mock';
import { Loader2, Check, Calendar, ArrowRight, Clock, Shield, Zap } from 'lucide-react';

const SubscriptionsPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    businessName: '',
    contactName: '',
    mobile: '',
    email: '',
    jobsPerWeek: '',
    billingPreference: '',
    areasServed: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    await mockSubmitForm('subscription', formData);
    setIsSubmitting(false);
    navigate('/thank-you/subscription');
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const benefits = [
    { icon: Calendar, title: 'Same great service', desc: 'Reliable collection from your customer sites' },
    { icon: Shield, title: 'Lower cost per job', desc: 'Save on each collection with volume commitment' },
    { icon: Clock, title: 'Up to 5 jobs/week', desc: 'Perfect for regular installers' },
    { icon: Zap, title: 'Overflow flexibility', desc: 'Additional jobs handled seamlessly' },
  ];

  return (
    <main className="bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#F8FAFC] to-white py-10 md:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#0D9488] text-white rounded-full text-sm font-medium mb-6">
              <Calendar className="w-4 h-4" />
              For Regular Installers
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#111111]">
              Subscription Plans
            </h1>
            <p className="mt-6 text-xl text-[#475569] leading-relaxed">
              Lower cost per job for installers doing regular work. Same reliable pickup service 
              with predictable costs.
            </p>
          </div>
        </div>
      </section>

      {/* How Subscription Works */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#111111] mb-4">How subscription works</h2>
          <p className="text-lg text-[#475569] mb-8 max-w-2xl">
            Same great collection service with a lower effective cost per job when you commit to regular volume.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((item, index) => (
              <Card key={index} className="card-hover border-[#E2E8F0]">
                <CardContent className="pt-6">
                  <div className="w-12 h-12 rounded-xl bg-[#F0FDFA] flex items-center justify-center mb-4">
                    <item.icon className="w-6 h-6 text-[#0D9488]" />
                  </div>
                  <h3 className="font-semibold text-[#111111]">{item.title}</h3>
                  <p className="mt-2 text-sm text-[#64748B]">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Ideal For */}
      <section className="section-padding bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-[#111111] mb-6">Ideal for installers who:</h2>
              <ul className="space-y-4">
                {[
                  'Complete 3-5+ battery installations per week',
                  'Want predictable, lower costs',
                  'Prefer not to pay per-job every time',
                  'Need reliable, scheduled pickups',
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#0D9488] flex-shrink-0 mt-0.5" />
                    <span className="text-[#475569]">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                <Link to="#enquiry-form">
                  <Button className="bg-[#0D9488] hover:bg-[#0F766E] text-white">
                    Enquire About Subscription
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 border border-[#E2E8F0] shadow-sm">
              <h3 className="text-xl font-bold text-[#111111] mb-6">What if I exceed 5 jobs?</h3>
              <p className="text-[#475569] leading-relaxed">
                No problem! Additional jobs beyond your subscription allowance can be handled as:
              </p>
              <ul className="mt-4 space-y-3">
                <li className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-[#64748B] flex-shrink-0 mt-0.5" />
                  <span className="text-[#475569]"><strong>Standard</strong> per-job pickup (within 7 days)</span>
                </li>
                <li className="flex items-start gap-3">
                  <Zap className="w-5 h-5 text-[#64748B] flex-shrink-0 mt-0.5" />
                  <span className="text-[#475569]"><strong>Priority</strong> pickup if urgent (within 48 hours)</span>
                </li>
              </ul>
              <p className="mt-4 text-sm text-[#64748B]">
                Flexibility to handle busy weeks without disrupting your workflow.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Enquiry Form */}
      <section id="enquiry-form" className="section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-3xl font-bold text-[#111111] text-center mb-4">Subscription Enquiry</h2>
            <p className="text-center text-[#475569] mb-8">
              Tell us about your business and we'll put together a subscription package.
            </p>
            
            <Card className="border-[#E2E8F0]">
              <CardContent className="pt-6">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Input
                      placeholder="Business name *"
                      value={formData.businessName}
                      onChange={(e) => handleChange('businessName', e.target.value)}
                      className="border-[#E2E8F0]"
                      required
                    />
                    <Input
                      placeholder="Contact name *"
                      value={formData.contactName}
                      onChange={(e) => handleChange('contactName', e.target.value)}
                      className="border-[#E2E8F0]"
                      required
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Input
                      type="tel"
                      placeholder="Mobile *"
                      value={formData.mobile}
                      onChange={(e) => handleChange('mobile', e.target.value)}
                      className="border-[#E2E8F0]"
                      required
                    />
                    <Input
                      type="email"
                      placeholder="Email *"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className="border-[#E2E8F0]"
                      required
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Select
                      value={formData.jobsPerWeek}
                      onValueChange={(value) => handleChange('jobsPerWeek', value)}
                    >
                      <SelectTrigger className="border-[#E2E8F0]">
                        <SelectValue placeholder="Jobs per week *" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="3-5">3-5 jobs/week</SelectItem>
                        <SelectItem value="6-10">6-10 jobs/week</SelectItem>
                        <SelectItem value="10+">10+ jobs/week</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select
                      value={formData.billingPreference}
                      onValueChange={(value) => handleChange('billingPreference', value)}
                    >
                      <SelectTrigger className="border-[#E2E8F0]">
                        <SelectValue placeholder="Billing preference" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Input
                    placeholder="Areas you serve (suburbs) *"
                    value={formData.areasServed}
                    onChange={(e) => handleChange('areasServed', e.target.value)}
                    className="border-[#E2E8F0]"
                    required
                  />
                  <Button 
                    type="submit" 
                    className="w-full bg-[#0D9488] hover:bg-[#0F766E] text-white h-12"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Submitting...</>
                    ) : (
                      'Submit Enquiry'
                    )}
                  </Button>
                  
                  <div className="text-center pt-4 border-t border-[#E2E8F0] mt-4">
                    <p className="text-sm text-[#64748B] mb-2">Ready to become a subscription client?</p>
                    <Link to="/subscribe">
                      <Button variant="outline" className="w-full border-[#0D9488] text-[#0D9488] hover:bg-[#F0FDFA]">
                        Start Your Registration <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </main>
  );
};

export default SubscriptionsPage;
